import React, { useMemo } from 'react'
import { compact, join } from 'lodash'
import { getRoleFromPath, timeDuration, viewMeetingDetail } from 'utils/helper'
import { IUSER_NOTIFICATION_TYPES, ROLES } from 'components/NotificationPanel/types'
import Button from 'components/Common/Button'
import {
  MessageTextWrapper,
  NotificationTextWrapper,
  PlayCommentWrapper,
  PlayCommentHeader,
  PlayCommentTitle,
  PlayCommentSummary,
  PlayCommentFooter,
} from 'components/NotificationPanel/style'
import { sanitize } from 'dompurify'
import { useHistory } from 'react-router-dom'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import NotificationMessageFactory from './notification-messages'
import { getTenantId } from 'utils/getTenantId'
import { AppButton } from 'components/Common/AppButton/AppButton.component'

// Format mentions as React elements for rich display
const formatMessageWithMentionsAsReact = (text: string) => {
  if (!text || typeof text !== 'string') return null

  // Regular expression to find mentions in the format <mention id="__id__">__display__</mention>
  const mentionRegex = /<mention id="([^"]*)">([^<]*)<\/mention>/g

  // If no mentions, just return the text as is
  if (!text.includes('<mention')) {
    return <span>{text}</span>
  }

  // Split the text at mentions and create React elements
  const parts: (string | JSX.Element)[] = []
  let lastIndex = 0

  // Find all mentions and replace them
  text.replace(mentionRegex, (match, id, displayName, index) => {
    // Add text before mention
    if (index > lastIndex) {
      parts.push(text.substring(lastIndex, index))
    }

    // Add styled mention - using more vivid colors and styling
    parts.push(
      <span
        key={`mention-${index}`}
        className="mention-highlight"
        style={{
          color: '#2563eb',
          fontWeight: 600,
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          padding: '2px 4px',
          borderRadius: '4px',
          display: 'inline-block',
          margin: '0 2px',
        }}
      >
        @{displayName}
      </span>
    )

    lastIndex = index + match.length
    return match
  })

  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }

  return <>{parts}</>
}

const Message = ({ notification, handleClose }) => {
  const userName = useMemo(() => {
    return join(compact([notification?.firstName, notification?.lastName]), ' ')
  }, [notification])
  const user = useSelector((state: RootState) => state.user.user)
  const isAdvisor = user?.roles.includes(ROLES.BSO_ADVISOR)
  // Extract initials for avatar
  const tenantId = getTenantId()
  const userInitials = useMemo(() => {
    const firstName = notification?.firstName || ''
    const lastName = notification?.lastName || ''
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }, [notification])
  const history = useHistory()

  // common notification content
  const renderNotificationContent = () => {
    if ([IUSER_NOTIFICATION_TYPES.CALENDAR_NOTE_TAGGED].includes(notification.notificationType)) {
      return (
        <PlayCommentWrapper>
          <PlayCommentHeader>
            <span className="font-medium text-app-primary-75">Notes</span>
            <span className="text-app-grey-60 text-xs">{timeDuration(notification.createdAt)}</span>
          </PlayCommentHeader>
          <div className="mt-2 break-words">
            {NotificationMessageFactory.getMessage(
              notification.notificationType,
              userName,
              notification.businessName
            )}
          </div>
          <PlayCommentTitle className="mt-2 break-words">
            {notification.meta.taskName || 'Play Name'}
          </PlayCommentTitle>
          {notification.meta.goalSummary && (
            <PlayCommentSummary className="mt-1.5 break-words">
              {notification.meta.goalSummary}
            </PlayCommentSummary>
          )}
          <PlayCommentFooter className="mt-3">
            <Button
              variant="text"
              label="View comment"
              className="text-app-primary-75 px-4 py-1.5 rounded-full text-sm hover:bg-app-primary-10 transition-colors duration-200"
              onClick={() => {
             

                history.push(
                  `/${getRoleFromPath()}/${tenantId}/clients/${
                    notification.meta.clientId
                  }/meetingRecordV2?filter=past&meetingId=${notification.meta.calendarId}`
                )

                handleClose()
               
              }}
            />
          </PlayCommentFooter>
        </PlayCommentWrapper>
      )
    }

    if (
      [
        IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_ADDED,
        IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_REPLY,
        IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_MENTION,
        IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_REPLY_MENTION,
        IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_THREAD,
        IUSER_NOTIFICATION_TYPES.MEETING_ACTION_ITEM_ADD,
      ].includes(notification.notificationType)
    ) {
      return (
        <PlayCommentWrapper>
          <PlayCommentHeader>
            <span className="font-medium text-app-primary-75">Plays</span>
            <span className="text-app-grey-60 text-xs">{timeDuration(notification.createdAt)}</span>
          </PlayCommentHeader>
          <div className="mt-2 break-words">
            {NotificationMessageFactory.getMessage(
              notification.notificationType,
              userName,
              notification.businessName
            )}
          </div>
          <PlayCommentTitle className="mt-2 break-words">
            {notification.meta.taskName || 'Play Name'}
          </PlayCommentTitle>
          {notification.meta.goalSummary && (
            <PlayCommentSummary className="mt-1.5 break-words">
              {notification.meta.goalSummary}
            </PlayCommentSummary>
          )}
          <PlayCommentFooter className="mt-3">
            <Button
              variant="text"
              label="View comment"
              className="text-app-primary-75 px-4 py-1.5 rounded-full text-sm hover:bg-app-primary-10 transition-colors duration-200"
              onClick={() => {
                if (notification.meta.taskId) {
                  
                  if (isAdvisor) {
                    history.push(
                      `/${getRoleFromPath()}/${tenantId}/clients/${notification.meta.businessId}/${
                        notification.meta.goalId || 'other-tasks'
                      }/task?selectedTaskId=${notification.meta.taskId}`
                    )
                  } else {
                    history.push(
                      `/${getRoleFromPath()}/${tenantId}/${
                        notification.meta.goalId || 'other-tasks'
                      }/task?selectedTaskId=${notification.meta.taskId}`
                    )
                  }
                  handleClose()
                }
              }}
            />
          </PlayCommentFooter>
        </PlayCommentWrapper>
      )
    }

    if (
      [
        IUSER_NOTIFICATION_TYPES.SUGGESTED_GOAL_MESSAGE,
        IUSER_NOTIFICATION_TYPES.ACCEPTED_GOAL_MESSAGE,
        IUSER_NOTIFICATION_TYPES.DECLINED_GOAL_MESSAGE,
      ].includes(notification.notificationType)
    ) {
      return (
        <NotificationContainer
          headerText="Goal Center"
          contentTitle={`${notification.meta.text.split('\n')[0]}`}
          contentValue={`${notification.meta.text.split('\n')[1]}`}
          onAccept={() => {
            history.push(notification.url)
            handleClose()
          }}
          onAcceptButtonLabel="Check it out"
          timestamp={`${timeDuration(notification.createdAt)}`}
        />
      )
    }

    if (
      [
        IUSER_NOTIFICATION_TYPES.SUGGESTED_PLAY_MESSAGE,
        IUSER_NOTIFICATION_TYPES.ACCEPTED_PLAY_MESSAGE,
        IUSER_NOTIFICATION_TYPES.DECLINED_PLAY_MESSAGE,
      ].includes(notification.notificationType)
    ) {
      return (
        <NotificationContainer
          headerText="Play"
          contentTitle={`${notification.meta.text.split('\n')[0]}`}
          contentValue={`${notification.meta.text.split('\n')[1]}`}
          contentSubValue={`${notification.meta.text.split('\n')[2]}`}
          onAccept={() => {
            history.push(notification.url)
            handleClose()
          }}
          onAcceptButtonLabel="Check it out"
          timestamp={`${timeDuration(notification.createdAt)}`}
        />
      )
    }

    return (
      <div className="py-1">
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CALENDAR_MEETING && (
          <>
            <MessageTextWrapper>
              {userName} {notification.businessName ? `of ${notification.businessName} ` : ''}
            </MessageTextWrapper>
            has scheduled a meeting
          </>
        )}
        {notification.notificationType ===
          IUSER_NOTIFICATION_TYPES.CALENDAR_MEETING_RESCHEDULED && (
          <>
            <MessageTextWrapper>
              {userName} {notification.businessName ? `of ${notification.businessName} ` : ''}
            </MessageTextWrapper>
            rescheduled a meeting
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CALENDAR_MEETING_CANCELLED && (
          <>
            <MessageTextWrapper>
              {userName} {notification.businessName ? `of ${notification.businessName} ` : ''}
            </MessageTextWrapper>
            cancelled a meeting
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.USER_DISABLED && (
          <>
            <MessageTextWrapper>
              The account for {`${userName} `}
              {notification.businessName ? `of ${notification.businessName} ` : ''}
            </MessageTextWrapper>
            has been disabled.
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CHAT && (
          <>
            <MessageTextWrapper>
              {userName} {notification.businessName ? `of ${notification.businessName} ` : ''}
            </MessageTextWrapper>
            sent a chat
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CHAT_TAG && (
          <NotificationContainer
            headerText="Messages"
            contentTitle={`${userName} tagged you in a message:`}
            contentValueReact={formatMessageWithMentionsAsReact(
              notification.meta?.messageText || notification.meta?.text || ''
            )}
            onAccept={() => {
              const tenantId = getTenantId()
              history.push(`/${getRoleFromPath()}/${tenantId}/chat`)
              handleClose()
            }}
            onAcceptButtonLabel="Check it out"
            timestamp={`${timeDuration(notification.createdAt)}`}
          />
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CHAT_MESSAGE_EDITED && (
          <NotificationContainer
            headerText="Messages"
            contentTitle={`${userName} edited a message:`}
            contentValueReact={formatMessageWithMentionsAsReact(
              notification.meta?.messageText || notification.meta?.text || ''
            )}
            onAccept={() => {
              const tenantId = getTenantId()
              history.push(`/${getRoleFromPath()}/${tenantId}/chat`)
              handleClose()
            }}
            onAcceptButtonLabel="View Chat"
            timestamp={`${timeDuration(notification.createdAt)}`}
          />
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CLIENT_USER_PROFILE_UPDATE && (
          <>
            {notification?.meta?.notificationGeneratedBy === ROLES.BUSINESS_OWNER ? (
              <>
                <MessageTextWrapper>
                  {userName} {notification.businessName ? `of ${notification.businessName} ` : ''}
                </MessageTextWrapper>
                has updated their user profile details
              </>
            ) : (
              <>
                Personal profile details for
                <MessageTextWrapper>
                  {` ${userName}`}
                  {notification.businessName ? ` of ${notification.businessName} ` : ''}
                </MessageTextWrapper>
                have been updated.
              </>
            )}
          </>
        )}
        {notification.notificationType ===
          IUSER_NOTIFICATION_TYPES.CLIENT_BUSINESS_PROFILE_UPDATE && (
          <>
            {notification?.meta?.notificationGeneratedBy === ROLES.BUSINESS_OWNER ? (
              <>
                <MessageTextWrapper>
                  {userName} {notification.businessName ? ` of ${notification.businessName} ` : ''}
                </MessageTextWrapper>
                has updated their business profile details.
              </>
            ) : (
              <>
                Business profile details for
                <MessageTextWrapper>
                  {` ${userName}`}
                  {notification.businessName ? ` of ${notification.businessName} ` : ''}
                </MessageTextWrapper>
                have been updated.
              </>
            )}
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.NEW_CLIENT_ADDED && (
          <>
            <MessageTextWrapper> {`${userName} `} </MessageTextWrapper>
            has been assigned to you.
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CLIENT_REMOVED && (
          <>
            You have been unassigned
            <MessageTextWrapper>{` ${userName}`} </MessageTextWrapper>
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.MONTHLY_ATTESTATION_UPDATE && (
          <>
            <MessageTextWrapper> {userName} </MessageTextWrapper> {notification.meta.text}
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.ANNOUNCEMENT && (
          <>
            <MessageTextWrapper> {userName} </MessageTextWrapper>
            <span>sent an announcement: </span>
            <MessageTextWrapper>
              <NotificationTextWrapper>
                <div dangerouslySetInnerHTML={{ __html: sanitize(notification.meta.text) }} />
              </NotificationTextWrapper>
              {notification.meta.fileUrl && (
                <a
                  href={notification.meta.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-app-primary-75 hover:underline mt-2 inline-block"
                >
                  View Attachment
                </a>
              )}
            </MessageTextWrapper>
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.MONTHLY_ATTESTATION_TIME && (
          <>
            <MessageTextWrapper>{userName} </MessageTextWrapper>
            {notification.meta.text}
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.PLAN_START_DATE && (
          <>{notification.meta.text}</>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.SUGGESTED_GOAL_MESSAGE && (
          <>{notification.meta.text}</>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.ACCEPTED_GOAL_MESSAGE && (
          <>{notification.meta.text.split('@@@')[0]}</>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.DECLINED_GOAL_MESSAGE && (
          <>{notification.meta.text.split('@@@')[0]}</>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.INTERNAL_ANNOUNCEMENT && (
          <>
            <MessageTextWrapper>{userName} </MessageTextWrapper>
            <div dangerouslySetInnerHTML={{ __html: sanitize(notification.meta.html) }} />
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CALENDAR_MEETING_UPDATED && (
          <>
            <MessageTextWrapper>
              {userName} {notification.businessName ? `of ${notification.businessName} ` : ''}
            </MessageTextWrapper>
            edited a meeting
            <Button
              variant="text"
              label="View details"
              className="text-app-primary-75 px-3 py-1 rounded-full text-sm hover:bg-app-primary-10 transition-colors duration-200 mt-2"
              onClick={() => {
                viewMeetingDetail(notification, handleClose)
              }}
            />
          </>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-start gap-4 w-full">
      {/* Avatar component */}
      <div className="flex-shrink-0 mt-4">
        <AppAvatar
          size="md"
          type="mixed"
          shape="Rounded"
          text={userInitials}
          src={notification?.profilePicture}
        />
      </div>

      <div className="flex flex-col flex-grow w-[80%]">
        {renderNotificationContent()}
        {notification.createdAt &&
          ![
            IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_ADDED,
            IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_REPLY,
            IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_MENTION,
            IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_REPLY_MENTION,
            IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_THREAD,
            IUSER_NOTIFICATION_TYPES.SUGGESTED_GOAL_MESSAGE,
            IUSER_NOTIFICATION_TYPES.ACCEPTED_GOAL_MESSAGE,
            IUSER_NOTIFICATION_TYPES.DECLINED_GOAL_MESSAGE,
            IUSER_NOTIFICATION_TYPES.MEETING_ACTION_ITEM_ADD,
            IUSER_NOTIFICATION_TYPES.SUGGESTED_PLAY_MESSAGE,
            IUSER_NOTIFICATION_TYPES.ACCEPTED_PLAY_MESSAGE,
            IUSER_NOTIFICATION_TYPES.DECLINED_PLAY_MESSAGE,
            IUSER_NOTIFICATION_TYPES.CALENDAR_NOTE_TAGGED,
          ].includes(notification.notificationType) && (
            <div className="text-xs text-app-grey-60 mt-2">
              {timeDuration(notification.createdAt)}
            </div>
          )}
      </div>
    </div>
  )
}

interface INotificationContainerProps {
  headerText: string
  contentTitle: string
  contentValue?: string
  contentValueReact?: React.ReactNode
  contentSubValue?: string
  onAccept?: () => void
  onReject?: () => void
  onAcceptButtonLabel?: string
  onRejectButtonLabel?: string
  timestamp?: string
}

const NotificationContainer: React.FC<INotificationContainerProps> = ({
  headerText,
  contentTitle,
  contentValue,
  contentValueReact,
  contentSubValue,
  onAccept,
  onReject,
  onAcceptButtonLabel,
  onRejectButtonLabel,
  timestamp,
}) => {
  return (
    <div className="space-y-2.5 w-full">
      <div className="flex items-center justify-between">
        <h3 className="font-inter font-medium text-sm text-app-primary-75">{headerText}</h3>
        {timestamp && <span className="text-app-grey-60 text-xs">{timestamp}</span>}
      </div>
      <div className="font-inter font-normal text-sm text-app-grey-80">{contentTitle}</div>
      {(contentValue || contentValueReact) && (
        <div className="font-inter font-medium text-sm text-app-black-100 break-words bg-app-grey-5 p-2.5 rounded-lg">
          {contentValueReact || contentValue}
        </div>
      )}
      {contentSubValue && (
        <div className="font-inter font-normal text-sm text-app-grey-70">{contentSubValue}</div>
      )}
      {onAccept && (
        <div className="mt-3">
          <AppButton
            size="sm"
            variant="primary"
            label={onAcceptButtonLabel || 'Check it out'}
            onClick={onAccept}
          />
        </div>
      )}
    </div>
  )
}

export default Message
